import React, { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Star } from "lucide-react";

export default function AdminsPanel() {
  const [admins, setAdmins] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data.admins || []);
      setMe(data.me);
    } catch (err) { toast.error(errorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/admins", { name: form.name.trim(), email: form.email.trim() });
      toast.success("Admin added — they'll sign in via OTP");
      setForm({ name: "", email: "" });
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.name} (${a.email})?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Admin removed");
      await load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="admins-panel">
      <form onSubmit={submit} className="lg:col-span-2 card-surface">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-brand" /><div className="font-display text-xl text-white">Add Admin</div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-mono block mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input-field" data-testid="admin-form-name" />
          </div>
          <div>
            <label className="label-mono block mb-1">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="input-field" placeholder="any email — gmail, college, etc." data-testid="admin-form-email" />
            <p className="text-xs text-gray-500 mt-1.5 font-mono">// No password — admin signs in with a one-time code at /login</p>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="add-admin-btn">
            {busy ? <span className="spinner" /> : <Plus className="w-4 h-4" />} Add Admin
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 card-surface overflow-x-auto" data-testid="admins-table">
        <div className="label-mono mb-3">Admin Accounts</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pr-4">Name</th>
              <th className="py-3 pr-4">Email</th>
              <th className="py-3 pr-4">Type</th>
              <th className="py-3 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${a.id}`}>
                <td className="py-3 pr-4 text-white">{a.name}{a.id === me && <span className="ml-2 text-[10px] font-mono text-brand">(you)</span>}</td>
                <td className="py-3 pr-4 text-gray-400">{a.email}</td>
                <td className="py-3 pr-4">
                  {a.is_primary
                    ? <span className="badge badge-accepted flex items-center gap-1 w-fit"><Star className="w-3 h-3" /> Primary</span>
                    : <span className="badge badge-upcoming">Admin</span>}
                </td>
                <td className="py-3 pr-4">
                  {a.is_primary || a.id === me ? (
                    <span className="text-gray-600 text-xs">—</span>
                  ) : (
                    <button onClick={() => remove(a)} className="text-gray-500 hover:text-[#EF4444]" data-testid={`remove-admin-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {admins.length === 0 && <tr><td colSpan={4} className="text-center text-gray-500 py-8">No admins found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
